export type PvcConfiguration = {
  widthCm: number;
  heightCm: number;
  quantity: number;
  materialId: string;
  addonIds: string[];
  requestedDate: string | null;
};

export type ProductionEstimate = {
  earliestReadyDate: string;
  selectedDateAvailable: boolean | null;
  rush?: {
    available: boolean;
    addonId: string | null;
    surchargeMinor: number | null;
    earliestReadyDate: string | null;
  };
};

export type PricePreview = {
  clientRevision: number;
  requestFingerprint: string;
  configurationHash: string;
  pricingVersion: string;
  quoteExpiresAt: string;
  currencyCode: "ILS";
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  includesVat: boolean;
  unitPriceMinor: number;
  pricePerSqmMinor: number | null;
  areaPerUnitSqm: number;
  totalAreaSqm: number;
  minChargeApplied: boolean;
  minimumChargeMinor: number;
  productionEstimate: ProductionEstimate;
};

export type PvcQuoteDomainRejection = {
  clientRevision: number;
  requestFingerprint: string;
  code: "CONFIGURATION_INVALID" | "PRODUCT_SCHEMA_CHANGED" | "OPTION_UNAVAILABLE" | "DATE_UNAVAILABLE";
  fieldErrors: Array<{ field: string; code: string }>;
  invalidOptionIds: string[];
  earliestAvailableDate: string | null;
  currentSchemaVersion: number | null;
};

export type PricingFailureCode = "NETWORK" | "OFFLINE" | "QUOTE_EXPIRED" | "SCHEMA_CHANGED" | "SESSION_CHANGED" | "NO_RETRY_REQUEST" | "UNKNOWN";

export type PricingFailure = { code: PricingFailureCode; message?: string; cause?: unknown };

export type PricingState =
  | { status: "idle"; quote: null }
  | { status: "initial-loading"; quote: null }
  | { status: "ready"; quote: PricePreview }
  | { status: "recalculating"; quote: PricePreview }
  | { status: "rejected"; quote: null; rejection: PvcQuoteDomainRejection }
  | { status: "stale"; quote: PricePreview; error: PricingFailure }
  | { status: "error"; quote: null; error: PricingFailure };

export type PricingInvalidationReason = "CONFIG_CHANGED" | "CONFIG_INVALID" | "SCHEMA_CHANGED" | "QUOTE_EXPIRED" | "SESSION_CHANGED";

export type PvcQuoteInput = {
  configuration: PvcConfiguration;
  clientRevision: number;
  requestFingerprint: string;
  schemaVersion: number;
};

export type QuoteGatewayResult =
  | { type: "success"; quote: PricePreview }
  | { type: "rejected"; rejection: PvcQuoteDomainRejection };

export interface QuoteGateway {
  fetch(input: PvcQuoteInput, signal?: AbortSignal): Promise<QuoteGatewayResult>;
}

export interface Clock { now(): number }
export interface Scheduler {
  setTimeout(callback: () => void, delayMs: number): unknown;
  clearTimeout(id: unknown): void;
}

export type PricingQuoteOutcome =
  | { type: "accepted"; quote: PricePreview }
  | { type: "rejected"; rejection: PvcQuoteDomainRejection }
  | { type: "failed"; failure: PricingFailure }
  | { type: "discarded"; reason: "superseded" | "disposed" };

type LastPricingRequest = { configuration: PvcConfiguration; schemaVersion: number };

export interface PricingController {
  getState(): PricingState;
  quote(configuration: PvcConfiguration, schemaVersion: number): Promise<PricingQuoteOutcome>;
  retry(): Promise<PricingQuoteOutcome>;
  invalidate(reason: PricingInvalidationReason, currentConfiguration?: PvcConfiguration): void;
  isCurrentQuoteValid(configuration: PvcConfiguration): boolean;
  subscribe(listener: (state: PricingState) => void): () => void;
  dispose(): void;
}

const MAX_TIMEOUT_MS = 2_147_483_647;
const MAX_IMMEDIATE_EXPIRY_RETRIES = 2;

const defaultClock: Clock = { now: () => Date.now() };

const defaultScheduler: Scheduler = {
  setTimeout(callback, delayMs) { return globalThis.setTimeout(callback, delayMs); },
  clearTimeout(id) { globalThis.clearTimeout(id as ReturnType<typeof setTimeout>); },
};

export function createConfigurationFingerprint(configuration: PvcConfiguration): string {
  return JSON.stringify({
    widthCm: configuration.widthCm,
    heightCm: configuration.heightCm,
    quantity: configuration.quantity,
    materialId: configuration.materialId,
    addonIds: [...configuration.addonIds].sort(),
    requestedDate: configuration.requestedDate,
  });
}

function cloneConfiguration(configuration: PvcConfiguration): PvcConfiguration {
  return { ...configuration, addonIds: [...configuration.addonIds] };
}

function getVisibleQuote(state: PricingState): PricePreview | null {
  switch (state.status) {
    case "ready":
    case "recalculating":
    case "stale":
      return state.quote;
    default:
      return null;
  }
}

function mapThrownFailure(error: unknown): PricingFailure {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { code: "OFFLINE", cause: error };
  }
  return { code: "NETWORK", cause: error };
}

export function createPricingController(options: { gateway: QuoteGateway; clock?: Clock; scheduler?: Scheduler }): PricingController {
  const gateway = options.gateway;
  const clock = options.clock ?? defaultClock;
  const scheduler = options.scheduler ?? defaultScheduler;

  let state: PricingState = { status: "idle", quote: null };
  let clientRevision = 0;
  let currentConfigFingerprint: string | null = null;
  let lastRequested: LastPricingRequest | null = null;
  let disposed = false;
  let activeAbortController: AbortController | null = null;
  let expiryTimer: unknown | null = null;
  let immediateExpiryRetryCount = 0;

  const listeners = new Set<(state: PricingState) => void>();

  function emit(nextState: PricingState) {
    if (disposed) return;
    state = nextState;
    for (const listener of listeners) listener(state);
  }

  function clearExpiryTimer() {
    if (expiryTimer !== null) { scheduler.clearTimeout(expiryTimer); expiryTimer = null; }
  }

  function quoteIsExpired(quote: PricePreview): boolean {
    const expiryMs = Date.parse(quote.quoteExpiresAt);
    if (!Number.isFinite(expiryMs)) return false;
    return expiryMs <= clock.now();
  }

  /**
   * Already-expired quote. Retries automatically a bounded number of times so
   * small clock skew recovers, then stops so a misbehaving server cannot be
   * turned into a request storm by every open tab.
   */
  function handleAlreadyExpiredQuote(quote: PricePreview) {
    emit({ status: "stale", quote, error: { code: "QUOTE_EXPIRED" } });

    if (immediateExpiryRetryCount >= MAX_IMMEDIATE_EXPIRY_RETRIES) return;

    immediateExpiryRetryCount++;
    void retry().catch(() => {});
  }

  function scheduleExpiry(quote: PricePreview) {
    clearExpiryTimer();

    const expiryMs = Date.parse(quote.quoteExpiresAt);
    if (!Number.isFinite(expiryMs)) return;

    const remainingMs = expiryMs - clock.now();
    if (remainingMs <= 0) { handleAlreadyExpiredQuote(quote); return; }

    // Timers are capped at 2^31-1 ms. Longer TTLs get a checkpoint that
    // re-arms instead of silently collapsing to a 1 ms timeout.
    const delay = Math.min(remainingMs, MAX_TIMEOUT_MS);

    expiryTimer = scheduler.setTimeout(() => {
      expiryTimer = null;
      if (disposed) return;
      if (state.status !== "ready") return;
      if (state.quote.configurationHash !== quote.configurationHash) return;

      if (!quoteIsExpired(quote)) { scheduleExpiry(quote); return; }

      invalidate("QUOTE_EXPIRED");
      void retry().catch(() => {});
    }, delay);
  }

  function markFailure(failure: PricingFailure, previousQuote: PricePreview | null) {
    if (previousQuote) { emit({ status: "stale", quote: previousQuote, error: failure }); return; }
    emit({ status: "error", quote: null, error: failure });
  }

  async function quote(configuration: PvcConfiguration, schemaVersion: number): Promise<PricingQuoteOutcome> {
    if (disposed) return { type: "discarded", reason: "disposed" };

    const storedConfiguration = cloneConfiguration(configuration);
    lastRequested = { configuration: storedConfiguration, schemaVersion };
    const revision = ++clientRevision;
    const fingerprint = createConfigurationFingerprint(storedConfiguration);
    currentConfigFingerprint = fingerprint;
    const previousQuote = getVisibleQuote(state);

    clearExpiryTimer();
    activeAbortController?.abort();
    const abortController = new AbortController();
    activeAbortController = abortController;

    if (previousQuote) emit({ status: "recalculating", quote: previousQuote });
    else emit({ status: "initial-loading", quote: null });

    let result: QuoteGatewayResult;
    try {
      result = await gateway.fetch(
        { configuration: storedConfiguration, schemaVersion, clientRevision: revision, requestFingerprint: fingerprint },
        abortController.signal
      );
    } catch (error) {
      if (disposed) return { type: "discarded", reason: "disposed" };
      if (revision !== clientRevision || fingerprint !== currentConfigFingerprint) {
        return { type: "discarded", reason: "superseded" };
      }
      const failure = mapThrownFailure(error);
      markFailure(failure, previousQuote);
      return { type: "failed", failure };
    }

    if (disposed) return { type: "discarded", reason: "disposed" };

    const responseRevision = result.type === "success" ? result.quote.clientRevision : result.rejection.clientRevision;
    const responseFingerprint = result.type === "success" ? result.quote.requestFingerprint : result.rejection.requestFingerprint;

    if (responseRevision !== revision) return { type: "discarded", reason: "superseded" };
    if (revision !== clientRevision) return { type: "discarded", reason: "superseded" };
    if (responseFingerprint !== currentConfigFingerprint) return { type: "discarded", reason: "superseded" };

    if (result.type === "rejected") {
      clearExpiryTimer();
      immediateExpiryRetryCount = 0;
      emit({ status: "rejected", quote: null, rejection: result.rejection });
      return { type: "rejected", rejection: result.rejection };
    }

    const acceptedQuote = result.quote;

    // A quote that is already expired on arrival is not a usable quote.
    if (quoteIsExpired(acceptedQuote)) {
      handleAlreadyExpiredQuote(acceptedQuote);
      return { type: "failed", failure: { code: "QUOTE_EXPIRED" } };
    }

    immediateExpiryRetryCount = 0;
    emit({ status: "ready", quote: acceptedQuote });
    scheduleExpiry(acceptedQuote);
    return { type: "accepted", quote: acceptedQuote };
  }

  async function retry(): Promise<PricingQuoteOutcome> {
    if (!lastRequested) {
      return { type: "failed", failure: { code: "NO_RETRY_REQUEST" } };
    }
    return quote(lastRequested.configuration, lastRequested.schemaVersion);
  }

  function invalidate(reason: PricingInvalidationReason, currentConfiguration?: PvcConfiguration) {
    if (disposed) return;
    clientRevision++;
    activeAbortController?.abort();
    activeAbortController = null;
    clearExpiryTimer();

    if (currentConfiguration) {
      currentConfigFingerprint = createConfigurationFingerprint(currentConfiguration);
    }

    const previousQuote = getVisibleQuote(state);

    if (reason === "CONFIG_INVALID") {
      immediateExpiryRetryCount = 0;
      emit({ status: "idle", quote: null });
      return;
    }

    if (reason === "CONFIG_CHANGED") {
      immediateExpiryRetryCount = 0;
      if (previousQuote) emit({ status: "recalculating", quote: previousQuote });
      else emit({ status: "idle", quote: null });
      return;
    }

    if (!previousQuote) { emit({ status: "idle", quote: null }); return; }

    const code: PricingFailureCode =
      reason === "QUOTE_EXPIRED" ? "QUOTE_EXPIRED" : reason === "SCHEMA_CHANGED" ? "SCHEMA_CHANGED" : "SESSION_CHANGED";

    emit({ status: "stale", quote: previousQuote, error: { code } });
  }

  function isCurrentQuoteValid(configuration: PvcConfiguration): boolean {
    if (state.status !== "ready") return false;
    const fingerprint = createConfigurationFingerprint(configuration);
    if (fingerprint !== state.quote.requestFingerprint) return false;
    if (fingerprint !== currentConfigFingerprint) return false;
    const expiryMs = Date.parse(state.quote.quoteExpiresAt);
    if (!Number.isFinite(expiryMs) || expiryMs <= clock.now()) return false;
    return true;
  }

  function subscribe(listener: (state: PricingState) => void) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    clientRevision++;
    activeAbortController?.abort();
    activeAbortController = null;
    clearExpiryTimer();
    listeners.clear();
  }

  return { getState: () => state, quote, retry, invalidate, isCurrentQuoteValid, subscribe, dispose };
}
